import { useEffect, useRef, useState } from "react";

/**
 * Hook for smooth loading transitions with minimum duration.
 * Returns both immediate and delayed loading states for flexible UI control.
 */
export const useSmoothLoading = (isLoading: boolean, minDuration = 300) => {
  const [shouldShowLoading, setShouldShowLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const loadingStartRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isLoading) {
      // Start loading immediately
      loadingStartRef.current = Date.now();
      setShouldShowLoading(true);

      // Make visible after a tiny delay for CSS transition
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else if (shouldShowLoading) {
      // Calculate remaining time to meet minimum duration
      const elapsed = Date.now() - loadingStartRef.current;
      const remaining = Math.max(0, minDuration - elapsed);

      // Start fade out
      setIsVisible(false);

      // Remove from DOM after transition completes
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setShouldShowLoading(false);
      }, remaining + 300); // 300ms for CSS transition
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [isLoading, shouldShowLoading, minDuration]);

  return {
    shouldShowLoading,
    isVisible,
    isActuallyLoading: isLoading,
  };
};
